import { LinkError } from '@apollo/client/errors'
import { DatePipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  EMPTY,
  Subject,
  catchError,
  finalize,
  map,
  of,
  switchMap,
  take,
} from 'rxjs'

import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import {
  KpiDashboardDefinition,
  KpiDashboardService,
  KpiMonthlyOrgTarget,
  KpiMonthlyOrgTargets,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { captureException } from '../../../util/captureException'
import {
  OrgTargetField,
  OrgTargetRow,
  buildOrgTargetFields,
  buildOrgTargetInputs,
  buildOrgTargetRows,
  describeOrgTargetFormState,
  findEffectiveOrgTarget,
  getCurrentOrgTargetPeriod,
  validateOrgTargetForm,
} from './kpi-org-target-settings.logic'

type LoadResult =
  | { success: true; targets: KpiMonthlyOrgTargets; resetFields: boolean }
  | { success: false; error: unknown }

@UntilDestroy()
@Component({
  selector: 'app-kpi-org-target-settings',
  standalone: true,
  imports: [DatePipe, FormsModule],
  templateUrl: './kpi-org-target-settings.component.html',
  styleUrl: './kpi-org-target-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiOrgTargetSettingsComponent implements OnInit {
  @Input({ required: true }) dashboard!: KpiDashboardDefinition

  period = getCurrentOrgTargetPeriod()
  fields: OrgTargetField[] = []
  rows: OrgTargetRow[] = []
  isLoading = true
  isSaving = false
  isDeletingPeriod: string | null = null
  loadErrorMessage = ''
  saveErrorMessage = ''
  private readonly reload$ = new Subject<boolean>()

  constructor(
    private kpiDashboardService: KpiDashboardService,
    private messageDialogService: MessageDialogService,
    private snackBar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.reload$
      .pipe(
        switchMap((resetFields) =>
          this.kpiDashboardService.orgTargets(this.dashboard.id).pipe(
            map(
              (targets): LoadResult => ({
                success: true,
                targets,
                resetFields,
              }),
            ),
            catchError((error: unknown) =>
              of<LoadResult>({ success: false, error }),
            ),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        this.isLoading = false
        if (result.success) {
          this.rows = buildOrgTargetRows(this.dashboard.items, result.targets)
          if (this.fields.length === 0 || result.resetFields) {
            this.onPeriodChanged()
          }
        } else {
          captureException(result.error)
          this.loadErrorMessage = errorMessage(
            result.error,
            '組織目標を読み込めませんでした。',
          )
        }
        this.changeDetectorRef.markForCheck()
      })
    this.load()
  }

  get isBusy(): boolean {
    return this.isSaving || this.isDeletingPeriod != null
  }

  get validationMessage(): string {
    return validateOrgTargetForm(this.period, this.fields)
  }

  get formStatus(): string {
    return describeOrgTargetFormState(this.period, this.effectiveTarget)
  }

  private get effectiveTarget(): KpiMonthlyOrgTarget | null {
    return findEffectiveOrgTarget(
      this.rows.map((row) => row.source),
      this.period,
    )
  }

  load(resetFields = false): void {
    this.isLoading = true
    this.loadErrorMessage = ''
    this.reload$.next(resetFields)
  }

  onPeriodChanged(): void {
    this.fields = buildOrgTargetFields(
      this.dashboard.items,
      this.effectiveTarget,
    )
    this.saveErrorMessage = ''
  }

  /** number input が不正文字を空文字として返す場合も、未設定と区別する。 */
  onTargetInput(field: OrgTargetField, event: Event): void {
    const input = event.target
    if (!(input instanceof HTMLInputElement))
      throw new Error('Target input is missing')
    if (input.validity.badInput) {
      // バインド値を維持し、変更検知で入力途中の文字列が消えるのを防ぐ。
      field.hasInvalidInput = true
    } else {
      field.hasInvalidInput = false
      field.targetRate = input.value === '' ? null : input.valueAsNumber
    }
    this.saveErrorMessage = ''
  }

  edit(row: OrgTargetRow): void {
    if (this.isBusy || this.isLoading) return
    this.period = row.source.period
    this.onPeriodChanged()
  }

  save(): void {
    if (
      this.isBusy ||
      this.isLoading ||
      this.loadErrorMessage !== '' ||
      this.validationMessage !== ''
    )
      return
    const period = this.period
    const targets = buildOrgTargetInputs(this.fields)
    this.isSaving = true
    this.saveErrorMessage = ''
    const confirmation$ =
      targets.length === 0
        ? this.messageDialogService.showConfirm(
            'この月以降の組織目標を解除します（すべて未設定になります）。よろしいですか？',
            {
              title: '組織目標の解除',
              primaryButtonText: '解除する',
              secondaryButtonText: 'キャンセル',
            },
          )
        : of(DialogResult.PrimaryButtonClicked)
    confirmation$
      .pipe(
        take(1),
        switchMap((result) =>
          result === DialogResult.PrimaryButtonClicked
            ? this.kpiDashboardService.saveOrgTarget(
                this.dashboard.id,
                period,
                targets,
              )
            : EMPTY,
        ),
        finalize(() => {
          this.isSaving = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.load(true)
          this.snackBar.open(
            targets.length === 0
              ? '組織目標を解除しました。'
              : '組織目標を保存しました。',
            '閉じる',
            { duration: 3000 },
          )
        },
        error: (error: unknown) =>
          this.handleOperationError(error, '組織目標を保存できませんでした。'),
      })
  }

  delete(row: OrgTargetRow): void {
    if (this.isBusy || this.isLoading || this.loadErrorMessage !== '') return
    const resetFields = row.source.period === this.effectiveTarget?.period
    this.isDeletingPeriod = row.source.period
    this.saveErrorMessage = ''
    this.messageDialogService
      .showConfirm(
        `${row.periodLabel}の組織目標を削除します。削除した月は直前の月の設定を引き継ぎます（直前の設定が無ければ未設定になります）。よろしいですか？`,
        {
          title: '組織目標の削除',
          primaryButtonText: '削除する',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(
        take(1),
        switchMap((result) =>
          result === DialogResult.PrimaryButtonClicked
            ? this.kpiDashboardService.deleteOrgTarget(
                this.dashboard.id,
                row.source.period,
              )
            : EMPTY,
        ),
        finalize(() => {
          this.isDeletingPeriod = null
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          // false も既に行が存在しない状態なので、最新の一覧へ更新する。
          this.load(resetFields)
          this.snackBar.open('組織目標を削除しました。', '閉じる', {
            duration: 3000,
          })
        },
        error: (error: unknown) =>
          this.handleOperationError(error, '組織目標を削除できませんでした。'),
      })
  }

  private handleOperationError(error: unknown, message: string): void {
    captureException(error)
    this.saveErrorMessage = errorMessage(error, message)
    this.changeDetectorRef.markForCheck()
  }
}

/** サーバーのエラーを表示し、メッセージを持たない例外とリンク由来（通信断・非 2xx 応答）のエラーのみ状況を補足する。 */
function errorMessage(error: unknown, message: string): string {
  return error instanceof Error && !LinkError.is(error) && error.message !== ''
    ? error.message
    : message
}
